import { useCallback, useMemo } from 'react';

import { InputNumber } from 'primereact/inputnumber';
import { IRpeMethod } from 'types/rpe/rpe-method-assigned';

export interface RpeOptionData {
  label: string;
  value: number;
  color: string | null;
  emoji: string | null;
}

/**
 * Construye las opciones del dropdown para un método RPE/RIR. Si el método
 * trae `valuesMeta` poblado, usa esos colores/emojis; si no (legacy o método
 * custom del coach sin metadata), genera valores planos a partir de
 * minValue/maxValue/step.
 *
 * Función pura para que sea testeable sin renderizar PrimeReact.
 */
export const getRpeOptionsForMethod = (
  method: Pick<IRpeMethod, 'minValue' | 'maxValue' | 'step' | 'valuesMeta'> | null | undefined
): RpeOptionData[] => {
  if (!method) return [];

  if (!method.valuesMeta?.length) {
    const count = (method.maxValue - method.minValue) / method.step + 1;
    return Array.from({ length: count }, (_, index) => {
      const value = method.minValue + index * method.step;
      return {
        label: value.toString(),
        value,
        color: null,
        emoji: null
      };
    });
  }

  return method.valuesMeta.map((meta) => ({
    label: meta.value.toString(),
    value: meta.value,
    color: meta.color ?? null,
    emoji: meta.emoji ?? null
  }));
};

/**
 * Acepta colores con o sin `#` y devuelve el formato canónico CSS (`#rrggbb`).
 * Antes el componente concatenaba `#${color}` ciegamente y los seeds que
 * guardan ya el `#` quedaban con `##rrggbb` (CSS inválido, swatch invisible).
 */
export const normalizeRpeColor = (color: string | null | undefined): string | null => {
  if (!color) return null;
  return color.startsWith('#') ? color : `#${color}`;
};

/**
 * Decide el color del texto (blanco/negro) según el contraste con el fondo.
 * Heurística simple basada en luminancia relativa.
 */
const pickReadableTextColor = (hexColor: string | null): string => {
  if (!hexColor || !hexColor.startsWith('#') || hexColor.length < 7) {
    return 'var(--ios-text)';
  }
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  // YIQ luminance — < 128 → fondo oscuro → texto blanco; ≥ 128 → texto negro.
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 140 ? '#ffffff' : '#0f172a';
};

export interface RpeDropdownComponentProps {
  selectedRpe: number | null | undefined;
  // eslint-disable-next-line no-unused-vars
  onChange: (event: { value: number | null }) => void;
  rpeMethod: IRpeMethod;
}

/**
 * Input para registrar la medición subjetiva del esfuerzo en una serie.
 *
 * Render:
 * - ≤ 10 valores: fila de chips clickeables con el color del valuesMeta.
 *   Visible al primer vistazo, sin popover. Funciona en iOS Safari donde
 *   el Dropdown de PrimeReact tenía problemas de viewport.
 * - > 10 valores (raro): InputNumber estándar como fallback.
 */
export default function RpeDropdownComponent({ selectedRpe, onChange, rpeMethod }: RpeDropdownComponentProps) {
  const options = useMemo(() => getRpeOptionsForMethod(rpeMethod), [rpeMethod]);

  const handleNumberChange = useCallback(
    (e: { value: number | null | undefined }) => {
      const value = e.value;
      if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
        onChange({ value: null });
        return;
      }
      if (value < rpeMethod.minValue || value > rpeMethod.maxValue) {
        onChange({ value: null });
        return;
      }
      onChange({ value });
    },
    [onChange, rpeMethod]
  );

  const useInputNumber = options.length > 10;

  return (
    <div className="rpe-dropdown w-full" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <span
        style={{
          fontSize: '0.78rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          color: 'var(--ios-text-secondary)',
          textTransform: 'uppercase'
        }}
      >
        {rpeMethod.name}
      </span>

      {useInputNumber ? (
        <InputNumber
          value={selectedRpe ?? null}
          min={rpeMethod.minValue}
          max={rpeMethod.maxValue}
          step={rpeMethod.step}
          onValueChange={handleNumberChange}
          placeholder="Valor"
          className="p-inputnumber-sm p-inputtext-sm w-full"
          showButtons
          buttonLayout="horizontal"
          decrementButtonClassName="p-button-secondary p-button-sm"
          incrementButtonClassName="p-button-secondary p-button-sm"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
          allowEmpty={true}
        />
      ) : (
        <div
          role="radiogroup"
          aria-label={rpeMethod.name}
          className="rpe-chip-row"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.35rem',
            alignItems: 'center'
          }}
        >
          {options.map((option) => {
            const selected = option.value === selectedRpe;
            const swatch = normalizeRpeColor(option.color);
            const textColor = swatch ? pickReadableTextColor(swatch) : 'var(--ios-text)';
            const background = swatch ?? 'var(--ios-surface-muted, rgba(0,0,0,0.05))';
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`rpe-chip-${option.value}`}
                onClick={() => onChange({ value: selected ? null : option.value })}
                style={{
                  minWidth: 36,
                  height: 36,
                  padding: '0 0.5rem',
                  borderRadius: 9999,
                  border: selected
                    ? '2px solid var(--ios-text, #0f172a)'
                    : '1px solid var(--ios-divider, rgba(0,0,0,0.08))',
                  background,
                  color: textColor,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: selected ? '0 0 0 3px rgba(163, 230, 53, 0.18)' : 'none',
                  // Touch tweaks para iOS Safari:
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                <span>{option.label}</span>
                {option.emoji && <span aria-hidden>{option.emoji}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
